import { type Cover, type GlassesRenderingOptions } from "types"
import uzip from "uzip"

const DIGITIZED_FILE_NAMES = {
  configJs: "config.js",
  renderingOptions: "renderingOptions.json",
  matFrontBaseColor: "mat_front_BaseColor.png",
  matLeftBaseColor: "mat_left_BaseColor.png",
  matRightBaseColor: "mat_right_BaseColor.png",
} as const

export type ConvertedGlassesZip = {
  coverFront: Cover | null
  coverLeft: Cover | null
  coverRight: Cover | null
}

export const zip = (files: Record<string, Uint8Array>) => {
  const archive = uzip.encode(files)
  return new Blob([archive], { type: "application/zip" })
}

export const unzip = async (archive: Blob): Promise<Record<string, Uint8Array>> => {
  const buffer = await archive.arrayBuffer()
  const files = uzip.parse(buffer)
  const filtered = Object.entries(files)
    .filter(([name]) => !name.includes("__MACOSX"))
    .filter(([name]) => !name.endsWith("/"))
    .filter(([_, data]) => data?.length)

  return Object.fromEntries(filtered) as Record<string, Uint8Array>
}

export const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      resolve(reader?.result?.toString() ?? "")
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const findArchivePathBySuffix = (
  files: Record<string, Uint8Array>,
  suffix: string
): string | undefined => {
  const normalizedSuffix = suffix.toLowerCase()

  return Object.keys(files).find((name) => name.toLowerCase().endsWith(normalizedSuffix))
}

const parseJson = <T>(value: Uint8Array | undefined): T | null => {
  if (!value) return null

  return JSON.parse(new TextDecoder().decode(value)) as T
}

export const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = ""
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i])

  return btoa(binary)
}

export const createGlassesFileDataFromUint8Array = async (
  fileData: Uint8Array,
  fileName: string
): Promise<Cover> => {
  const fileBase64 = uint8ArrayToBase64(fileData)

  return {
    fileBase64: `data:image/png;base64,${fileBase64}`,
    lastModified: new Date().getTime(),
    name: fileName,
    size: fileData.length,
    type: "image/png",
  }
}

const createArchivePatch = async (
  files: Record<string, Uint8Array>
): Promise<ConvertedGlassesZip> => {
  const coverLeftPath = findArchivePathBySuffix(files, DIGITIZED_FILE_NAMES.matLeftBaseColor)
  const coverFrontPath = findArchivePathBySuffix(files, DIGITIZED_FILE_NAMES.matFrontBaseColor)
  const coverRightPath = findArchivePathBySuffix(files, DIGITIZED_FILE_NAMES.matRightBaseColor)

  return {
    coverFront: coverFrontPath
      ? await createGlassesFileDataFromUint8Array(
          files[coverFrontPath],
          DIGITIZED_FILE_NAMES.matFrontBaseColor
        )
      : null,
    coverLeft: coverLeftPath
      ? await createGlassesFileDataFromUint8Array(
          files[coverLeftPath],
          DIGITIZED_FILE_NAMES.matLeftBaseColor
        )
      : null,
    coverRight: coverRightPath
      ? await createGlassesFileDataFromUint8Array(
          files[coverRightPath],
          DIGITIZED_FILE_NAMES.matRightBaseColor
        )
      : null,
  }
}

export const convertGlassesZipToObject = async (
  zipUrl: string
): Promise<Partial<ConvertedGlassesZip>> => {
  try {
    const archive = await fetch(zipUrl).then((res) => res.blob())
    const files = await unzip(archive)
    return await createArchivePatch(files)
  } catch {
    return {}
  }
}

const updateRenderingOptionsFile = (
  currentValue: Record<string, unknown> | null,
  renderingOptions: GlassesRenderingOptions
): Record<string, unknown> => ({
  ...(currentValue ?? {}),
  ...(renderingOptions.bodyGloss !== undefined ? { bodyGloss: renderingOptions.bodyGloss } : {}),
  ...(renderingOptions.lensesGloss !== undefined
    ? { lensesGloss: renderingOptions.lensesGloss }
    : {}),
  ...(renderingOptions.transparency !== undefined
    ? { transparency: renderingOptions.transparency }
    : {}),
  positionAccuracy: {
    x: renderingOptions.positionAccuracy.x,
    y: renderingOptions.positionAccuracy.y,
    z: renderingOptions.positionAccuracy.z,
  },
  scale: {
    x: renderingOptions.scale.x,
    y: renderingOptions.scale.x,
    z: renderingOptions.scale.x,
  },
})

const formatNumber = (value: number): string => {
  return Number(value.toFixed(6)).toString()
}

const MANAGED_TRANSFORM_BLOCK_START = "// tint-platform transform overrides:start"
const MANAGED_TRANSFORM_BLOCK_END = "// tint-platform transform overrides:end"
const managedTransformBlockRegex =
  /(?:\r?\n){0,2}[ \t]*\/\/ tint-platform transform overrides:start[\s\S]*?[ \t]*\/\/ tint-platform transform overrides:end[ \t]*(?:\r?\n)?/g

const replaceLastStandaloneCall = (
  value: string,
  callRegex: RegExp,
  replacement: string,
  lineEnding: string
): string => {
  const matches = Array.from(value.matchAll(callRegex))
  if (matches.length === 0) return `${value.trimEnd()}${lineEnding}${lineEnding}${replacement}`

  const match = matches[matches.length - 1]
  const matchIndex = match.index ?? 0

  return `${value.slice(0, matchIndex)}${replacement}${value.slice(matchIndex + match[0].length)}`
}

const updateConfigJsDefaults = (
  configJs: string,
  renderingOptions: GlassesRenderingOptions
): string => {
  const lineEnding = configJs.includes("\r\n") ? "\r\n" : "\n"
  const cleanConfigJs = configJs.replace(managedTransformBlockRegex, "").trimEnd()
  const scale = formatNumber(renderingOptions.scale.x)
  const scaleCall = `setScale(${scale})`
  const scaleRegex = /^[ \t]*setScale\([^)]*\)\s*;?[ \t]*$/gm
  const configJsWithScale = replaceLastStandaloneCall(
    cleanConfigJs,
    scaleRegex,
    scaleCall,
    lineEnding
  ).trimEnd()
  const {
    positionAccuracy: { x, y, z },
  } = renderingOptions
  const translationCall = `setTranslation(${formatNumber(x)}, ${formatNumber(y)}, ${formatNumber(
    z
  )})`

  return [
    configJsWithScale,
    "",
    MANAGED_TRANSFORM_BLOCK_START,
    translationCall,
    MANAGED_TRANSFORM_BLOCK_END,
    "",
  ].join(lineEnding)
}

export const createDigitizedGlassesZipEffect = async ({
  effectUrl,
  renderingOptions,
}: {
  effectUrl: string
  renderingOptions: GlassesRenderingOptions
}) => {
  const archive = await fetch(effectUrl).then((res) => res.blob())
  const files = await unzip(archive)
  const configJsPath = findArchivePathBySuffix(files, DIGITIZED_FILE_NAMES.configJs)
  const renderingOptionsPath = findArchivePathBySuffix(files, DIGITIZED_FILE_NAMES.renderingOptions)

  if (!configJsPath) throw new Error("Couldn't update this try-on")

  const configJs = new TextDecoder().decode(files[configJsPath])
  const renderingOptionsFile = parseJson<Record<string, unknown>>(
    renderingOptionsPath ? files[renderingOptionsPath] : undefined
  )

  if (!configJs) throw new Error("Couldn't update this try-on")

  const nextConfigJs = updateConfigJsDefaults(configJs, renderingOptions)

  files[configJsPath] = new TextEncoder().encode(nextConfigJs)
  if (renderingOptionsPath)
    files[renderingOptionsPath] = new TextEncoder().encode(
      JSON.stringify(updateRenderingOptionsFile(renderingOptionsFile, renderingOptions), null, 2)
    )

  return zip(files)
}
